<template>
    <pageWithNav>
        <innerPage :innerTopNav="true">
            <div slot="page" class="admin-wrapper">
                <div class="admin-page">
                    <!-- STATS -->
                    <div class="stats">
                        <el-card class="stat">
                            <div class="stat-title">Organizations</div>
                            <div class="stat-value">{{ formatNumber(totals.organizations) }}</div>
                        </el-card>

                        <el-card class="stat">
                            <div class="stat-title">Users</div>
                            <div class="stat-value">{{ formatNumber(totals.users) }}</div>
                        </el-card>

                        <el-card class="stat">
                            <div class="stat-title">Workflows</div>
                            <div class="stat-value">{{ formatNumber(totals.workflows) }}</div>
                        </el-card>

                        <el-card class="stat">
                            <div class="stat-title">Executions</div>
                            <div class="stat-value">{{ formatNumber(totals.executions) }}</div>
                        </el-card>
                    </div>

                    <div class="chart-toolbar">
                        <el-date-picker v-model="dateRange" type="daterange" range-separator="To"
                            start-placeholder="Start" end-placeholder="End" @change="loadChart" />
                    </div>

                    <execution-chart :labels="chartLabels" :data-points="chartValues" />

                    <div class="content organization-content">
                        <!-- LEFT -->
                        <el-card class="left">
                            <div style="margin-bottom: 12px;" class="section-title">Organizations</div>

                            <el-empty :image="require('../assets/empty.svg')" v-if="!organizations.length"
                                description="No Organizations"></el-empty>

                            <el-table v-else :data="organizations" height="600px" highlight-current-row
                                @current-change="selectOrganization">
                                <el-table-column prop="organisationName" label="Organization" />
                            </el-table>
                        </el-card>

                        <!-- RIGHT -->
                        <el-card class="right">
                            <!-- USERS -->
                            <template v-if="selectedOrg && !selectedUser">
                                <div
                                    style="display: flex; justify-content: space-between; align-items: center; width: 100%;  margin-bottom: 12px;">
                                    <div class="section-title">
                                        Users in {{ selectedOrg.organisationName }}
                                    </div>
                                    <el-button type="primary" @click="exportUsers">
                                        Export Users
                                    </el-button>
                                </div>
                                <el-input v-model="userSearch" placeholder="Search user" clearable
                                    style="margin-bottom: 10px" />
                                <div v-if="usersLoading" class="empty">Loading...</div>

                                <div v-else-if="!orgUsers.length" class="empty" style="margin-top: 40px;">
                                    <el-empty :image="require('../assets/empty.svg')" description="No users"></el-empty>
                                </div>
                                <el-table v-else :data="orgUsers" height="480" highlight-current-row
                                    @current-change="selectUser">
                                    <el-table-column prop="username" label="User" width="170" />
                                    <el-table-column prop="email" label="Email" />

                                    <el-table-column label="Role" width="100">
                                        <template #default="scope">
                                            <el-tag v-if="scope.row.admin" type="danger">
                                                Admin
                                            </el-tag>
                                            <el-tag v-else>Member</el-tag>
                                        </template>
                                    </el-table-column>

                                    <el-table-column label="Account Approved" width="140">
                                        <template #default="scope">
                                            <el-tag v-if="scope.row.accountApproved" type="success">
                                                Yes
                                            </el-tag>
                                            <el-tag v-else>No</el-tag>
                                        </template>
                                    </el-table-column>

                                    <el-table-column label="Email Verified" width="110">
                                        <template #default="scope">
                                            <el-tag v-if="scope.row.emailVerified" type="success">
                                                Yes
                                            </el-tag>
                                            <el-tag v-else>No</el-tag>
                                        </template>
                                    </el-table-column>

                                    <el-table-column label="Workflows" width="90">
                                        <template #default="scope">
                                            {{ (scope.row.workflows || 0) }}
                                        </template>
                                    </el-table-column>

                                    <el-table-column label="Actions" width="100">
                                        <template #default="scope">
                                            <template v-if="(scope.row.workflows || 0) === 0">
                                                <el-button type="danger" size="mini"
                                                    @click.stop="openUserDeleteDialog(scope.row)">
                                                    Delete
                                                </el-button>
                                            </template>

                                            <template v-else>
                                                -
                                            </template>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </template>

                            <!-- WORKFLOWS -->
                            <template v-else-if="selectedUser">
                                <div
                                    style="display: flex; justify-content: space-between; align-items: center; width: 100%;  margin-bottom: 12px;">
                                    <div class="section-title workflow-header">
                                        <el-button link @click="selectedUser = null">
                                            <i class="el-icon-back" />
                                        </el-button>
                                        Workflows of {{ selectedUser.username }}
                                    </div>
                                    <el-button type="primary" size="mini" @click="exportWorkflows">
                                        Export Workflows
                                    </el-button>
                                </div>

                                <div v-if="workflowLoading" class="empty">
                                    Loading...
                                </div>

                                <div v-else-if="!userWorkflows.length" class="empty" style="margin-top: 40px;">
                                    <el-empty :image="require('../assets/empty.svg')"
                                        description="No workflows"></el-empty>
                                </div>

                                <template v-else>
                                    <el-table :data="userWorkflows" style="width: 100%" height="480">
                                        <el-table-column prop="name" label="Workflow" />

                                        <el-table-column label="Active" width="120">
                                            <template #default="scope">
                                                <el-tag v-if="scope.row.isActive" type="success">
                                                    Yes
                                                </el-tag>
                                                <el-tag v-else type="info">No</el-tag>
                                            </template>
                                        </el-table-column>

                                        <el-table-column prop="executionCount" label="Executions" width="140" />

                                        <el-table-column label="Last Updated" width="230">
                                            <template #default="scope">
                                                {{ formatDate(scope.row.updatedAt) }}
                                            </template>
                                        </el-table-column>

                                        <el-table-column label="Actions" width="200">
                                            <template #default="scope">
                                                <el-button size="mini" type="primary"
                                                    @click.stop="openExportWorkflow(scope.row)">
                                                    Export
                                                </el-button>

                                                <el-button size="mini" type="danger"
                                                    @click.stop="openWorkflowDeleteDialog(scope.row)">
                                                    Delete
                                                </el-button>
                                            </template>
                                        </el-table-column>
                                    </el-table>

                                    <div class="pager">
                                        <el-pagination background layout="sizes, prev, pager, next" :pager-count="5"
                                            :page-sizes="[10, 20, 50]" :page-size="resultsPerPage" :total="totalRecords"
                                            :current-page="page + 1" @current-change="pageChange"
                                            @size-change="sizeChange" />
                                    </div>
                                </template>
                            </template>

                            <template v-else>
                                <div style="margin-top: 80px;">
                                    <el-empty :image="require('../assets/empty.svg')"
                                        description="Select an Organizations"></el-empty>
                                </div>
                            </template>
                        </el-card>
                    </div>
                    <el-dialog title="Export Workflow" :visible.sync="showExportModal" width="420px"
                        :close-on-click-modal="false">
                        <div>
                            <label style="margin-bottom:8px;display:block;">Select version</label>

                            <el-select v-model="selectedExportVersion" placeholder="Select version" style="width:100%">
                                <el-option v-for="v in exportVersions" :key="v.versionId"
                                    :label="v.versionName && v.versionName !== v.version ? `${v.versionName} (${v.version})` : v.version"
                                    :value="v.versionId" />
                            </el-select>
                        </div>

                        <span slot="footer">
                            <el-button @click="showExportModal = false">Cancel</el-button>
                            <el-button type="primary" :disabled="!selectedExportVersion" @click="confirmExportWorkflow">
                                Export
                            </el-button>
                        </span>
                    </el-dialog>
                    <el-dialog title="Delete Workflow" :visible.sync="workflowDeleteDialog" width="420px"
                        :close-on-click-modal="false">
                        <p>
                            Type <b>{{ workflowToDelete?.name }}</b> to confirm deletion.
                        </p>

                        <el-input v-model="workflowDeleteText" placeholder="Enter workflow name" />

                        <span slot="footer">
                            <el-button @click="workflowDeleteDialog = false">Cancel</el-button>

                            <el-button type="danger" :disabled="workflowDeleteText !== workflowToDelete?.name"
                                @click="deleteWorkflowConfirmed">
                                Delete
                            </el-button>
                        </span>
                    </el-dialog>
                    <el-dialog title="Delete User" :visible.sync="userDeleteDialog" width="420px"
                        :close-on-click-modal="false">
                        <p>
                            Type <b>{{ userToDelete?.username }}</b> to confirm deletion.
                        </p>

                        <el-input v-model="userDeleteText" placeholder="Enter username" />

                        <span slot="footer">
                            <el-button @click="userDeleteDialog = false">Cancel</el-button>

                            <el-button type="danger" :disabled="userDeleteText !== userToDelete?.username"
                                @click="deleteUserConfirmed">
                                Delete
                            </el-button>
                        </span>
                    </el-dialog>
                </div>
            </div>
        </innerPage>
    </pageWithNav>
</template>

<script>
import ExecutionChart from "@/components/ExecutionChart.vue";
import pageWithNav from "../components/pageWithNav.vue";
import innerPage from "../components/innerPage.vue";
import { dateToLocalFormat } from 'date-format-ms';
import { mapActions } from "vuex"
import Vue from "vue";
import { DatePicker } from "element-ui";

export default {
    name: "admin_page",
    components: {
        pageWithNav,
        innerPage,
        ExecutionChart,
        "el-date-picker": DatePicker
    },
    data() {
        return {
            orgLoading: false,
            workflowLoading: false,
            selectedOrg: null,
            selectedUser: null,
            organizations: [],
            userWorkflows: [],
            page: 0,
            resultsPerPage: 10,
            totalRecords: 0,
            globalWorkflowCount: 0,
            globalExecutionCount: 0,
            userSearch: "",
            orgUsersFromApi: [],
            usersLoading: false,
            dateRange: [],
            chartLabels: [],
            chartValues: [],
            showExportModal: false,
            exportVersions: [],
            selectedExportVersion: null,
            exportWorkflowTarget: null,
            workflowDeleteDialog: false,
            workflowToDelete: null,
            workflowDeleteText: "",
            userDeleteDialog: false,
            userToDelete: null,
            userDeleteText: "",
        };
    },
    computed: {
        orgUsers() {
            let users = this.orgUsersFromApi || [];

            if (this.userSearch) {
                const s = this.userSearch.toLowerCase();
                users = users.filter(u =>
                    u.username.toLowerCase().includes(s)
                );
            }

            return users.map(u => ({
                ...u,
                admin: u.username === this.selectedOrg?.organisationAdminEmail
            }));
        },
        totals() {
            const orgs = this.organizations.length;

            const users = this.organizations.reduce(
                (sum, o) => sum + (o.users ? o.users.length : 0),
                0
            );

            return {
                organizations: orgs,
                users,
                workflows: this.globalWorkflowCount,
                executions: this.globalExecutionCount,
            };
        }
    },
    async mounted() {
        await this.fetchOrganizations();
        await this.fetchGlobalCounts();
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 6);

        this.dateRange = [start, end];
        await this.loadChart();

        this.adminSecretKey = "12345";
    },
    methods: {
        ...mapActions(["fetchWorkflowVersionsMETHOD"]),
        formatDate(dateString) {
            return dateToLocalFormat(new Date(dateString), "D jS M Y g:ia");
        },
        formatNumber(num) {
            if (num === null || num === undefined) return '0';
            return num.toLocaleString('en-US');
        },
        async fetchOrganizations() {
            this.orgLoading = true;
            try {
                const result = await this.httpHelper(
                    "RUNTIME",
                    "GET",
                    `security/fetchActiveOrganisations?email=${Vue.$cookies.get("email")}`,
                    {}
                );
                this.organizations = result.response || [];
            } catch (e) {
                this.organizations = [];
            }
            this.orgLoading = false;
        },

        async fetchGlobalCounts() {
            try {
                const result = await this.httpHelper(
                    "RUNTIME",
                    "GET",
                    "workflow/userSummary",
                    { adminEmail: Vue.$cookies.get("email") }
                );
                this.globalWorkflowCount =
                    result.response.globalWorkflowCount || 0;
                this.globalExecutionCount = result.response.globalExecutionCount || 0;
            } catch (e) {
                this.globalWorkflowCount = 0;
            }
        },

        async selectOrganization(org) {
            this.selectedOrg = org;
            this.selectedUser = null;
            await this.fetchUsersOfOrg();
        },

        async fetchUsersOfOrg() {
            if (!this.selectedOrg) return;

            this.usersLoading = true;

            try {
                const result = await this.httpHelper(
                    "RUNTIME",
                    "GET",
                    "security/fetchAllUsers",
                    { 
                        organisationName: this.selectedOrg.organisationName,
                        adminEmail: Vue.$cookies.get("email")
                    }
                );

                this.orgUsersFromApi = result.response || [];
            } catch (e) {
                this.orgUsersFromApi = [];
            }

            this.usersLoading = false;
        },

        async exportUsers() {
            const result = await this.httpHelper(
                "RUNTIME",
                "GET",
                "security/exportAllUsers",
                { 
                    organisationName: this.selectedOrg.organisationName,
                    adminEmail: Vue.$cookies.get("email")
                },
                null,
                { responseType: "blob" }
            );
            this.$notify({
                title: "Preparing Export",
                message: "Your export is being prepared. The download will start shortly. Please keep this page open.",
                type: "info",
                duration: 5000,
                position: "bottom-left",
            });
            const url = window.URL.createObjectURL(
                new Blob([result.response], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                })
            );

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "users.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        },

        async selectUser(user) {
            this.selectedUser = user;
            this.page = 0;
            await this.fetchUserWorkflows();
        },

        async fetchUserWorkflows() {
            this.workflowLoading = true;

            try {
                const result = await this.httpHelper(
                    "RUNTIME",
                    "GET",
                    "workflow/userSummary",
                    {
                        userID: this.selectedUser.userID,
                        page: this.page,
                        resultsPerPage: this.resultsPerPage,
                        adminEmail: Vue.$cookies.get("email")
                    }
                );

                this.userWorkflows = result.response.workflows || [];
                this.totalRecords = result.response.totalRecords || 0;
                this.globalWorkflowCount =
                    result.response.globalWorkflowCount || 0;
                this.globalExecutionCount = result.response.globalExecutionCount || 0;
            } catch (e) {
                this.userWorkflows = [];
            }

            this.workflowLoading = false;
        },

        async pageChange(p) {
            this.page = p - 1;
            await this.fetchUserWorkflows();
        },

        async sizeChange(size) {
            this.resultsPerPage = size;
            this.page = 0;
            await this.fetchUserWorkflows();
        },

        async loadChart() {
            if (!this.dateRange || !this.dateRange.length) return;

            const [start, end] = this.dateRange;

            const result = await this.httpHelper(
                "RUNTIME",
                "GET",
                "logs/summary",
                {
                    startDate: start,
                    endDate: end,
                    adminEmail: Vue.$cookies.get("email")
                }
            );

            const data = result.response || {};

            this.chartLabels = data.labels || [];
            this.chartValues = data.values || [];
        },
        async openExportWorkflow(workflow) {
            this.exportWorkflowTarget = workflow

            const versions = await this.fetchWorkflowVersionsMETHOD(workflow.workflowId)

            if (!versions || !versions.length) {
                this.$confirm("Export workflow?", "Export", {
                    confirmButtonText: "Export",
                    cancelButtonText: "Cancel",
                    type: "warning"
                }).then(() => {
                    this.exportWorkflowFile({ workflowId: workflow.workflowId })
                })
                return
            }

            this.exportVersions = versions
            const active = versions.find(v => v.isActive) || versions[0]
            this.selectedExportVersion = active?.versionId
            this.showExportModal = true
        },
        async confirmExportWorkflow() {
            const workflowId = this.exportWorkflowTarget.workflowId
            const versionId = this.selectedExportVersion

            this.showExportModal = false
            await this.exportWorkflowFile({ workflowId, versionId })
        },
        async exportWorkflowFile({ workflowId, versionId = null }) {
            const result = await this.httpHelper(
                "RUNTIME",
                "GET",
                "workflow/exportOne",
                versionId ? { workflowId, versionId } : { workflowId },
                null,
                { responseType: "blob" }
            )

            if (result?.error) {
                this.$notifyError(this, result.error);
                return;
            }

            const url = window.URL.createObjectURL(new Blob([result.response], { type: "application/zip" }))

            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", `workflow_${workflowId}_${versionId || "latest"}.zip`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        },
        async exportWorkflows() {
            this.$notify({
                title: "Preparing Export",
                message: "Your export is being prepared. The download will start shortly. Please keep this page open.",
                type: "info",
                duration: 5000,
                position: "bottom-left",
            });

            const result = await this.httpHelper(
                "RUNTIME",
                "GET",
                "workflow/exportUserWorkflows",
                { 
                    userID: this.selectedUser.userID,
                    adminEmail: Vue.$cookies.get("email")
                },
                null,
                { responseType: "blob" }
            );

            const url = window.URL.createObjectURL(
                new Blob([result.response], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                })
            );

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "workflows.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        },
        openWorkflowDeleteDialog(workflow) {
            this.workflowToDelete = workflow
            this.workflowDeleteText = ""
            this.workflowDeleteDialog = true
        },
        async deleteWorkflowConfirmed() {
            await this.httpHelper("RUNTIME", "DELETE", "workflow/deleteOne", {
                workflowId: this.workflowToDelete.workflowId,
                confirmText: this.workflowDeleteText,
                userId: this.selectedUser.userID
            })

            this.workflowDeleteDialog = false
            this.fetchUserWorkflows();
            this.fetchUsersOfOrg();
            await this.fetchGlobalCounts();
        },
        openUserDeleteDialog(user) {
            this.userToDelete = user
            this.userDeleteText = ""
            this.userDeleteDialog = true
        },
        async deleteUserConfirmed() {
            await this.httpHelper("RUNTIME", "DELETE", "security/deleteUser", {
                userID: this.userToDelete.userID,
                confirmText: this.userDeleteText,
                adminSecretKey: this.adminSecretKey,
                isOrgAdmin: true,
                organisationName: this.selectedOrg.organisationName,
                email: Vue.$cookies.get("email")
            })

            this.userDeleteDialog = false
            this.fetchUsersOfOrg();
            await this.fetchGlobalCounts();
        },
    }
};
</script>

<style scoped>
.admin-wrapper {
    width: 100%;
    overflow: hidden;
}

.admin-page {
    height: 82vh;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
}

.stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    flex-shrink: 0;
}

.stat {
    text-align: center;
}

.stat-title {
    font-size: 13px;
    color: #909399;
}

.stat-value {
    font-size: 26px;
    font-weight: 600;
    margin-top: 6px;
}

.content {
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 20px;
    height: 600px;
}

.left,
.right {
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.section-title {
    font-weight: 600;
}

.empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
}

.workflow-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.pager {
    margin-top: 10px;
    text-align: right;
}

.execution-chart {
    height: 300px;
    width: 100%;
    flex-shrink: 0;
}

.chart-toolbar {
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-end;
}

.organization-content {
    margin-top: 40px;
    flex: 1;
}

.workflow-header button {
    padding: 0;
    margin: 0;
    border: 0;
}

.workflow-header button:hover {
    background: transparent;
}

.workflow-header button i {
    font-size: 20px;
}
</style>
